<template>
    <div class="flex flex-wrap gap-12">
        <simple-input
            class="w-240"
            icon="search"
            :placeholder="text.search"
            v-model="filters.search"
        />
        <select-dropdown
            :auto-translate="false"
            class="w-240"
            :options="sortOptions"
            no-auto-sort
            v-model="filters.sortBy"
        />
        <select-dropdown
            :auto-translate="false"
            class="w-240"
            keep-search-after-selection
            :loading="fetchingJobTitles"
            :options="lookups.jobTitles"
            :placeholder="$t('multi-posting.campaigns.filters.job_title')"
            remove-icon
            searchable
            v-model="filters.jobTitleId"
            @search="search($event, 'jobtitles')"
        />
        <select-dropdown
            :auto-translate="false"
            class="w-240"
            keep-search-after-selection
            :options="lookups.jobFunctions"
            :placeholder="$t('multi-posting.campaigns.filters.job_function')"
            remove-icon
            searchable
            v-model="filters.jobFunctionId"
        />
        <select-dropdown
            :auto-translate="false"
            class="w-240"
            keep-search-after-selection
            :options="lookups.industries"
            :placeholder="$t('multi-posting.campaigns.filters.industries')"
            remove-icon
            searchable
            v-model="filters.industryId"
        />
        <select-dropdown
            :auto-translate="false"
            class="w-240"
            keep-search-after-selection
            :loading="fetchingLocations"
            :options="lookups.locations"
            :placeholder="$t('multi-posting.campaigns.filters.location')"
            remove-icon
            searchable
            v-model="filters.locationId"
            @search="search($event, 'locations')"
        />
    </div>
</template>

<script>
import SelectDropdown from "../forms/SelectDropdown.vue";
import SimpleInput from "../forms/Input.vue";
import SvgIcon from "../ui/SvgIcon.vue";
import {mapActions, mapState} from "pinia";
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import _ from "lodash";
import PaidSubscription from "../../models/PaidSubscription";
import debounce from "lodash/debounce";

export default {
    name: 'JobBoardFilters',
    components: {SimpleInput, SelectDropdown, SvgIcon},
    data: function () {
        return {
            selectedProduct: null,
            searchTimeout: null,
            fetchingJobTitles: false,
            fetchingLocations: false,
            debouncedFetch: null,
        }
    },
    computed: {
        ...mapState(useMultiPostingStore, ['products', 'filters', 'lookups', 'sortOptions', 'fetchingProducts']),
        ...mapState(useMainStore, ['currentCompany']),
        hasMultiPostingSubscription() {
            return !!this.multiPostingSubscription && this.currentCompany.multiposting_status === 'active';
        },
        multiPostingSubscription() {
            return useRepo(PaidSubscription)
                .where('type', 'multi-posting')
                .where('company_id', this.currentCompany.id)
                .where('active', true)
                .first();
        },
        text() {
            return {
                search: `${_.capitalize(this.$t('general.search').toString())}...`,
                channel: _.capitalize(this.$t('campaigns.channels').toString()),
                sortBy: _.capitalize(this.$t('general.sort_by').toString())
            }
        },
        sortOptions() {
            return [
                { value: 'relevant', label: this.$t('general.filters.relevant') },
                { value: 'recent', label: this.$t('general.filters.recent') },
                { value: 'order_frequency.asc', label: this.$t('general.filters.order_frequency.asc') },
                { value: 'order_frequency.desc', label: this.$t('general.filters.order_frequency.desc') },
                { value: 'created.asc', label: this.$t('general.filters.created_asc') },
                { value: 'created.desc', label: this.$t('general.filters.created_desc') },
                { value: 'list_price.asc', label: this.$t('general.filters.list_price_asc') },
                { value: 'list_price.desc', label: this.$t('general.filters.list_price_desc') },
            ];
        }
    },
    methods: {
        ...mapActions(useMultiPostingStore, ['resetPagination', 'fetchLookups', 'fetchProducts',
            'fetchTaxonomyJobTitles', 'fetchTaxonomyLocations']),
        viewDetails(product) {
            this.selectedProduct = product;
        },
        search(event, type) {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }

            this.searchTimeout = setTimeout(() => {
                switch (type) {
                    case 'jobtitles':
                        this.fetchingJobTitles = true;
                        this.fetchTaxonomyJobTitles(event)
                            .then(() => this.filters.jobFunctionId = this.lookups.jobTitles.find(x => x.value === this.filters.jobTitleId)?.jobFunction.id)
                            .finally(() => this.fetchingJobTitles = false);
                        break;
                    case 'locations':
                        this.fetchingLocations = true;
                        this.fetchTaxonomyLocations(event)
                            .finally(() => this.fetchingLocations = false);
                        break;
                }
                clearTimeout(this.searchTimeout);
            }, 250);
        }
    },
    watch: {
        filters: {
            handler() {
                // reset pagination if filters have changed
                this.resetPagination();
                this.debouncedFetch();
            },
            deep: true
        },
    },
    created() {
        this.debouncedFetch = debounce(() => {
            this.fetchProducts({pagination: this.pagination});
        }, 250);
    },
    mounted() {
        if (this.hasMultiPostingSubscription) {
            this.fetchLookups();
        }
    }
}
</script>